import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 페이지네이션 컴포넌트 (반응형)
 * 부모 요소의 너비에 따라 표시할 페이지 수를 자동으로 조정하고, 대량 페이지 처리 지원
 * @param {object} props
 * @param {number} props.currentPage - 현재 페이지 번호 (0-based)
 * @param {number} props.totalPages - 전체 페이지 수
 * @param {function} props.onPageChange - 페이지 변경 시 호출될 함수 (페이지 번호를 인자로 받음)
 *
 * @example
 * // Pagination 컴포넌트 사용 예시
 * import Pagination from './components/widgets/Pagination';
 * import { useState } from 'react';
 *
 * const MyPage = () => {
 *   const [currentPage, setCurrentPage] = useState(0);
 *   const totalPages = 10;
 *
 *   const handlePageChange = (page) => {
 *     setCurrentPage(page);
 *     // 페이지 변경 시 데이터 로드
 *     console.log('Page changed to:', page);
 *   };
 *
 *   return (
 *     <div>
 *       <Pagination
 *         currentPage={currentPage}
 *         totalPages={totalPages}
 *         onPageChange={handlePageChange}
 *       />
 *     </div>
 *   );
 * };
 */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [parentWidth, setParentWidth] = useState(0);

    // 부모 요소의 너비 감지
    useEffect(() => {
        if (!containerRef.current) return;

        let timeoutId;
        const resizeObserver = new ResizeObserver((entries) => {
            // 디바운싱으로 성능 최적화
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                for (const entry of entries) {
                    setParentWidth(entry.contentRect.width);
                }
            }, 150);
        });

        // 부모 요소 관찰
        const parentElement = containerRef.current.parentElement;
        if (parentElement) {
            resizeObserver.observe(parentElement);
            // 초기 너비 설정
            setParentWidth(parentElement.offsetWidth);
        }

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
        };
    }, []);

    // 부모 너비에 따라 최대 표시 페이지 수 계산
    const maxVisiblePages = useMemo(() => {
        if (parentWidth === 0) return 7; // 초기값

        // 페이지 버튼 하나당 대략 45px (여백 포함)
        const buttonWidth = 45;
        const navButtonsWidth = 90; // 이전/다음 버튼 2개
        const padding = 40; // 좌우 여백
        const availableWidth = parentWidth - navButtonsWidth - padding;
        const calculatedPages = Math.floor(availableWidth / buttonWidth);

        // 최소 3개, 최대 9개로 제한
        return Math.max(3, Math.min(9, calculatedPages));
    }, [parentWidth]);

    // 표시할 페이지 번호 계산 (대량 페이지 처리)
    const visiblePages = useMemo(() => {
        if (totalPages <= 1) return [];

        // 전체 페이지가 최대 표시 수보다 적으면 모두 표시
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i);
        }

        const pages = [];

        // 항상 표시: 첫 페이지, 마지막 페이지
        pages.push(0);

        // 현재 페이지 주변 계산
        const availableSlots = maxVisiblePages - 2; // 첫/마지막 제외
        const halfSlots = Math.floor(availableSlots / 2);

        let start = Math.max(1, currentPage - halfSlots);
        let end = Math.min(totalPages - 2, currentPage + halfSlots);

        // 양쪽 끝 조정
        if (start === 1) {
            // 시작이 첫 페이지 바로 다음이면 끝을 확장
            end = Math.min(totalPages - 2, start + availableSlots - 1);
        } else if (end === totalPages - 2) {
            // 끝이 마지막 바로 전이면 시작을 확장
            start = Math.max(1, end - availableSlots + 1);
        }

        // 생략 표시 추가
        if (start > 1) {
            pages.push('ellipsis-start');
        }

        // 중간 페이지들
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        // 생략 표시 추가
        if (end < totalPages - 2) {
            pages.push('ellipsis-end');
        }

        // 마지막 페이지
        pages.push(totalPages - 1);

        return pages;
    }, [totalPages, currentPage, maxVisiblePages]);

    if (totalPages <= 1) {
        return null;
    }

    return (
        <nav ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
            <ul className="pagination justify-content-center flex-wrap" style={{ marginBottom: 0 }}>
                <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => currentPage > 0 && onPageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                        aria-label={t('components.common.prevPage')}
                    >
                        &laquo;
                    </button>
                </li>
                {visiblePages.map((page, index) => {
                    if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                        return (
                            <li key={`ellipsis-${index}`} className="page-item disabled">
                                <span className="page-link" style={{ cursor: 'default', pointerEvents: 'none' }}>
                                    ...
                                </span>
                            </li>
                        );
                    }
                    return (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                                type="button"
                                className="page-link"
                                onClick={() => onPageChange(page)}
                                aria-label={t('components.pagination.pageN', { n: page + 1 })}
                                aria-current={currentPage === page ? 'page' : undefined}
                            >
                                {page + 1}
                            </button>
                        </li>
                    );
                })}
                <li className={`page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}`}>
                    <button
                        type="button"
                        className="page-link"
                        onClick={() => currentPage < totalPages - 1 && onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages - 1}
                        aria-label={t('components.common.nextPage')}
                    >
                        &raquo;
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export default Pagination;
